import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import GymMinimalBrandingFields from './GymMinimalBrandingFields';
import { OrgProvisionSuccessModal } from './OrgOperatorPanel';

const emptyForm = {
  nombre: '',
  brand_name: '',
  ciudad: '',
  email: '',
  admin_email: '',
  admin_password: '',
  logo_url: '',
  brand_slug: '',
  welcome_message: '',
  support_whatsapp: '',
  primary_color: '#2563eb',
  secondary_color: '#10b981',
};

export default function CreateVirtualGymModal({ open, onClose, onCreated, parentGym = null }) {
  const { t } = useI18n();
  const isSede = Boolean(parentGym?.id);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [cloneFromSeed, setCloneFromSeed] = useState(true);
  const [error, setError] = useState('');
  const [provisionModal, setProvisionModal] = useState(null);
  const [parentPrograms, setParentPrograms] = useState([]);
  const [calendarProgramIds, setCalendarProgramIds] = useState([]);
  const [memberFeeCop, setMemberFeeCop] = useState('');
  const [programsLoading, setProgramsLoading] = useState(false);
  const [sedePlans, setSedePlans] = useState([]);
  const [sedePlanId, setSedePlanId] = useState('');
  const [plansLoading, setPlansLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(emptyForm);
    setCalendarProgramIds([]);
    setMemberFeeCop('');
    setParentPrograms([]);
    setSedePlans([]);
    setSedePlanId('');
    setCloneFromSeed(true);
  }, [open]);

  useEffect(() => {
    if (!open || !isSede || !parentGym?.id) return undefined;
    let active = true;
    setProgramsLoading(true);
    api.get('/programs', { params: { gym_id: parentGym.id, gym_only: 'true' } })
      .then((res) => {
        if (!active) return;
        const list = res.data?.data || res.data || [];
        const arr = Array.isArray(list) ? list : [];
        setParentPrograms(arr);
        setCalendarProgramIds(arr.map((p) => p.id));
      })
      .catch(() => {
        if (active) {
          setParentPrograms([]);
          setCalendarProgramIds([]);
        }
      })
      .finally(() => {
        if (active) setProgramsLoading(false);
      });
    return () => { active = false; };
  }, [open, isSede, parentGym?.id]);

  useEffect(() => {
    if (!open || !isSede) return undefined;
    let active = true;
    setPlansLoading(true);
    Promise.all([
      api.get('/accounts/plans', {
        params: {
          kind: 'd28d',
          platform_only: 'true',
          include_for_sedes: 'true',
          visible: 'true',
        },
      }).catch(() => ({ data: [] })),
      parentGym?.id
        ? api.get('/accounts/plans', { params: { kind: 'd28d', gym_id: parentGym.id, visible: 'true' } }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
    ])
      .then(([platRes, gymRes]) => {
        if (!active) return;
        const a = Array.isArray(platRes.data) ? platRes.data : (platRes.data?.data || []);
        const b = Array.isArray(gymRes.data) ? gymRes.data : (gymRes.data?.data || []);
        const map = new Map();
        [...a, ...b].forEach((p) => {
          if (p?.for_sedes === true && p.activo !== false && p.visible !== false) {
            map.set(p.nombre, p);
          }
        });
        const list = [...map.values()].sort((x, y) => (x.sort_order || 0) - (y.sort_order || 0));
        setSedePlans(list);
        if (list.length === 1) setSedePlanId(list[0].nombre);
      })
      .finally(() => {
        if (active) setPlansLoading(false);
      });
    return () => { active = false; };
  }, [open, isSede, parentGym?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'email' && (!prev.admin_email || prev.admin_email === prev.email)) {
        next.admin_email = value;
      }
      return next;
    });
  };

  const toggleProgram = (programId) => {
    setCalendarProgramIds((prev) => {
      const id = String(programId);
      return prev.some((p) => String(p) === id)
        ? prev.filter((p) => String(p) !== id)
        : [...prev, programId];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSede && parentPrograms.length && !calendarProgramIds.length) {
      setError(t('orgs.sede_programs_required', 'Selecciona al menos un programa para el calendario de la sede.'));
      return;
    }
    if (isSede && !sedePlanId) {
      setError(t('orgs.sede_plan_required', 'Debes asignar un plan de sede. Será el único plan visible para los usuarios de esta sede.'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const contactEmail = form.email?.trim() || null;
      const payload = {
        nombre: form.nombre.trim(),
        ciudad: form.ciudad?.trim() || null,
        email: contactEmail,
        brand_name: form.brand_name?.trim() || form.nombre.trim(),
        logo_url: form.logo_url?.trim() || undefined,
        primary_color: form.primary_color || '#2563eb',
        secondary_color: form.secondary_color || '#10b981',
        support_whatsapp: form.support_whatsapp?.trim() || undefined,
        pais: 'Colombia',
        white_label_enabled: true,
        status: 'active',
        capacidad_usuarios: 0,
        available_plan_ids: [],
        plan_id: '',
        admin_email: form.admin_email?.trim() || contactEmail || undefined,
        admin_password: form.admin_password?.trim() || undefined,
      };
      if (isSede) {
        payload.parent_gym_id = Number(parentGym.id);
        payload.white_label_enabled = true;
        payload.calendar_program_ids = calendarProgramIds;
        payload.member_fee_cop = Math.max(0, Number(memberFeeCop) || 0);
        payload.skip_member_payment = true;
        payload.available_plan_ids = [sedePlanId];
        payload.plan_id = sedePlanId;
      } else if (cloneFromSeed) {
        payload.clone_from_seed = true;
      }
      const res = await api.post('/gyms', payload);
      const body = res.data || {};
      onCreated?.();
      onClose?.();
      if (body.admin_credentials || body.admin_user || body.invite_code) {
        setProvisionModal({
          title: isSede
            ? t('orgs.sede_created', 'Sede creada')
            : t('tenants.gym_created', 'Gimnasio virtual creado'),
          inviteCode: body.invite_code || body.gym?.invite_code,
          adminUser: body.admin_user,
          adminCredentials: body.admin_credentials,
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || t('tenants.create_error', 'No se pudo crear el gimnasio virtual'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="form-modal-overlay" style={{ zIndex: 9998 }} role="dialog" aria-modal="true" onClick={onClose}>
        <div
          className="form-modal d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="form-modal-header">
            <h3 className="font-bold text-stone-900">
              {isSede
                ? t('orgs.create_sede', 'Nueva sede')
                : t('tenants.create_title', 'Nuevo gimnasio virtual')}
            </h3>
            <button type="button" className="form-close-btn" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
          <form className="form-modal-content space-y-4" onSubmit={handleSubmit}>
            <p className="text-sm text-stone-600">
              {isSede
                ? t('orgs.create_sede_hint', 'Crea una sede bajo {org}. Hereda los programas del gimnasio virtual y elige cuáles verá en el calendario.').replace('{org}', parentGym?.name || parentGym?.nombre || '')
                : t('tenants.create_hint', 'Crea una organización con su administrador. Configura planes, marca y contacto desde el panel operativo.')}
            </p>
            {error ? <div className="alert-error text-sm">{error}</div> : null}
            <label className="block text-sm text-stone-800">
              <span className="font-semibold text-stone-800">
                {isSede ? t('orgs.field_sede_name', 'Nombre de la sede') : t('tenants.field_name', 'Nombre del gimnasio virtual')}
              </span>
              <input className="input mt-1 w-full" name="nombre" required value={form.nombre} onChange={handleChange} />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm text-stone-800">
                <span className="font-semibold text-stone-800">{t('tenants.field_city', 'Ciudad')}</span>
                <input className="input mt-1 w-full" name="ciudad" value={form.ciudad} onChange={handleChange} />
              </label>
              <label className="block text-sm text-stone-800">
                <span className="font-semibold text-stone-800">{t('tenants.field_contact_email', 'Email de contacto')}</span>
                <input className="input mt-1 w-full" type="email" name="email" value={form.email} onChange={handleChange} />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm text-stone-800">
                <span className="font-semibold text-stone-800">{t('tenants.field_admin_email', 'Email admin (login)')}</span>
                <input className="input mt-1 w-full" type="email" name="admin_email" value={form.admin_email} onChange={handleChange} placeholder={form.email || 'mismo que contacto'} />
                <span className="text-xs text-stone-500 mt-0.5 block">{t('tenants.admin_email_hint', 'Puede ser el mismo email de contacto; es el usuario con el que entras al panel.')}</span>
              </label>
              <label className="block text-sm text-stone-800">
                <span className="font-semibold text-stone-800">{t('tenants.field_admin_password', 'Contraseña admin (opcional)')}</span>
                <input className="input mt-1 w-full" type="password" name="admin_password" value={form.admin_password} onChange={handleChange} autoComplete="new-password" />
              </label>
            </div>
            {!isSede ? (
              <label className="flex items-start gap-2 text-sm text-stone-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={cloneFromSeed}
                  onChange={(e) => setCloneFromSeed(e.target.checked)}
                />
                <span>
                  <span className="font-semibold block">
                    {t('tenants.clone_from_seed', 'Clonar estructura maestra desde D28D-SEED')}
                  </span>
                  <span className="text-xs text-stone-500">
                    {t('tenants.clone_from_seed_hint', 'Copia programas, ciclos, rutinas, clases y retos plantilla. El gym queda independiente después de creado.')}
                  </span>
                </span>
              </label>
            ) : null}
            {isSede ? (
              <>
              <label className="block text-sm text-stone-800">
                <span className="font-semibold text-stone-800">{t('orgs.sede_plan', 'Plan de la sede')} *</span>
                <select
                  className="input mt-1 w-full"
                  required
                  value={sedePlanId}
                  disabled={plansLoading}
                  onChange={(e) => setSedePlanId(e.target.value)}
                >
                  <option value="">{plansLoading ? t('common.loading', 'Cargando...') : t('orgs.sede_plan_placeholder', 'Selecciona un plan de sedes')}</option>
                  {sedePlans.map((p) => (
                    <option key={p.nombre} value={p.nombre}>
                      {(p.display_nombre || p.nombre)}
                      {Number(p.precio_mensual) === 0 && Number(p.precio_mensual_usd) === 0
                        ? ' · $0'
                        : ` · $${Number(p.precio_mensual || 0).toLocaleString('es-CO')} COP`}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-stone-500 mt-0.5 block">
                  {t(
                    'orgs.sede_plan_hint',
                    'Obligatorio. Solo este plan se mostrará en registro y alta de usuarios de la sede. Debe estar marcado como Sedes en Planes D28D.',
                  )}
                </span>
                {!plansLoading && !sedePlans.length ? (
                  <span className="text-xs text-amber-700 mt-1 block">
                    {t('orgs.sede_plan_empty', 'No hay planes con check Sedes. Créalos en Maestros → Planes D28D antes de abrir la sede.')}
                  </span>
                ) : null}
              </label>
              <label className="block text-sm text-stone-800">
                <span className="font-semibold text-stone-800">{t('orgs.sede_member_fee', 'Valor de cobro (COP)')}</span>
                <input
                  className="input mt-1 w-full"
                  type="number"
                  min="0"
                  step="1000"
                  value={memberFeeCop}
                  onChange={(e) => setMemberFeeCop(e.target.value)}
                  placeholder="0"
                />
                <span className="text-xs text-stone-500 mt-0.5 block">
                  {t('orgs.sede_member_fee_hint', 'Referencia para cobro en sede. Los usuarios registrados por código no pagan en línea.')}
                </span>
              </label>
              <fieldset className="border border-slate-200 rounded-xl p-4 space-y-3">
                <legend className="text-sm font-semibold text-stone-800 px-1">
                  {t('orgs.sede_calendar_programs', 'Programas visibles en calendario')}
                </legend>
                <p className="text-xs text-stone-500">
                  {t('orgs.sede_calendar_programs_hint', 'Heredados del gimnasio virtual padre. La sede solo verá clases de los programas seleccionados.')}
                </p>
                {programsLoading ? (
                  <p className="text-sm text-stone-500">{t('common.loading', 'Cargando...')}</p>
                ) : parentPrograms.length ? (
                  <ul className="space-y-2 max-h-40 overflow-y-auto">
                    {parentPrograms.map((prog) => (
                      <li key={prog.id}>
                        <label className="flex items-center gap-2 text-sm text-stone-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={calendarProgramIds.some((id) => String(id) === String(prog.id))}
                            onChange={() => toggleProgram(prog.id)}
                          />
                          <span>{prog.name || prog.id}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-amber-700">
                    {t('orgs.sede_no_parent_programs', 'El gimnasio virtual padre aún no tiene programas. Crea programas en Maestros antes de abrir sedes.')}
                  </p>
                )}
              </fieldset>
              </>
            ) : null}
            <GymMinimalBrandingFields formData={form} setFormData={setForm} showBrandName />
            <div className="flex gap-2 pt-2">
              <button type="button" className="btn-secondary flex-1" onClick={onClose}>{t('common.cancel', 'Cancelar')}</button>
              <button type="submit" className="btn-primary flex-1" disabled={saving || (isSede && (programsLoading || plansLoading || !sedePlans.length))}>
                {saving ? t('common.saving', 'Guardando…') : (isSede ? t('orgs.create_sede_submit', 'Crear sede') : t('tenants.create_submit', 'Crear gimnasio virtual'))}
              </button>
            </div>
          </form>
        </div>
      </div>
      <OrgProvisionSuccessModal
        open={!!provisionModal}
        onClose={() => setProvisionModal(null)}
        title={provisionModal?.title}
        inviteCode={provisionModal?.inviteCode}
        adminUser={provisionModal?.adminUser}
        adminCredentials={provisionModal?.adminCredentials}
      />
    </>
  );
}
